import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowUpRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { news, assets } from "../data/mock";

function formatDate(s) {
  try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return s; }
}

export default function Blog() {
  const [hero, ...rest] = news;
  return (
    <div className="relative">
      <Header />
      <PageHero
        eyebrow="News & Insights"
        title="From the"
        accent="depot."
        subtitle="Project completions, awards, sustainability stories and updates from the R&C team."
        image={assets.sparks}
        crumbs={[{ label: "News" }]}
      />

      {/* Featured article */}
      <section className="py-16 lg:py-20 bg-[#08080a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link
            to={`/news/${hero.id}`}
            className="reveal group block rounded-2xl overflow-hidden bg-[#101014] border border-white/8 hover:border-[#F5D000]/40 transition-all"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                <img src={hero.cover} alt={hero.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute top-4 left-4">
                  <span className="px-2.5 py-1 rounded-full bg-[#F5D000] text-black text-[10px] font-semibold uppercase tracking-wider">
                    Featured · {hero.category}
                  </span>
                </div>
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-4 text-xs text-white/55 mb-4">
                  <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {formatDate(hero.date)}</span>
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {hero.readTime}</span>
                </div>
                <h2 className="font-display text-3xl lg:text-4xl font-bold leading-[1.1] mb-4 text-white group-hover:text-[#F5D000] transition-colors">
                  {hero.title}
                </h2>
                <p className="text-white/70 leading-relaxed mb-6">{hero.excerpt}</p>
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#F5D000]">
                  Read article <ArrowUpRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-24 lg:pb-32 bg-[#08080a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {rest.map((n, i) => (
              <Link
                key={n.id}
                to={`/news/${n.id}`}
                className="proj-card reveal block rounded-xl overflow-hidden bg-[#101014] border border-white/5 group"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={n.cover} alt={n.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-[#F5D000] text-black text-[10px] font-semibold uppercase tracking-wider">
                    {n.category}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-white/55 mb-3">
                    <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {formatDate(n.date)}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {n.readTime}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold mb-2 text-white group-hover:text-[#F5D000] transition-colors line-clamp-2">
                    {n.title}
                  </h3>
                  <p className="text-sm text-white/65 leading-relaxed line-clamp-3">{n.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
      <Footer />
    </div>
  );
}
